import { styled } from '@mui/material';
import { motion } from 'framer-motion';
import { useEffect, useRef } from 'react';

import { useMediaDevicesContext } from '../../providers/media-devices-context';
import { makeAudioContext } from '../../utils/make-audio-context';
import { TubeAnimation2D } from './tube-animation-2d';
import { TubeAnimation3D } from './tube-animation-3d';
import { ZaraAnimation2D } from './zara-animation-2d';
import { ZaraAnimation3D } from './zara-animation-3d';

const AvatarContainer = styled(motion.div)({
  position: 'absolute',
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  pointerEvents: 'none',
});

const AvatarCanvas = styled('canvas')<{ size: number }>(({ size }) => ({
  position: 'absolute',
  width: `${size}px`,
  height: `${size}px`,
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)',
}));

type ZaraAvatarProps = {
  isAiSpeaking: boolean;
  avServiceAnalyser?: AnalyserNode | null;
  isLoading?: boolean;
  size?: number;
  pointSize?: number;
};

type AudioAnalyzer = {
  analyserNode: AnalyserNode;
  cleanup: () => void;
};

const setupAudioAnalyzer = (
  stream: MediaStream | null,
  isAiSpeaking: boolean,
): AudioAnalyzer => {
  const audioContext = makeAudioContext();
  const analyserNode = audioContext.createAnalyser();
  const gainNode = audioContext.createGain();

  analyserNode.fftSize = 2048;
  analyserNode.smoothingTimeConstant = 0.5;

  gainNode.connect(analyserNode);
  gainNode.gain.value = isAiSpeaking ? 12.0 : 0.3;

  if (stream && stream.getAudioTracks().length > 0 && !isAiSpeaking) {
    const source = audioContext.createMediaStreamSource(stream);
    const compressor = audioContext.createDynamicsCompressor();
    compressor.threshold.value = -30;
    compressor.knee.value = 30;
    compressor.ratio.value = 8;
    compressor.attack.value = 0;
    compressor.release.value = 0.25;
    source.connect(compressor);
    compressor.connect(gainNode);
    return {
      analyserNode,
      cleanup: () => {
        source.disconnect();
        compressor.disconnect();
        gainNode.disconnect();
        analyserNode.disconnect();
        audioContext.close();
      },
    };
  } else {
    const oscillator = audioContext.createOscillator();
    oscillator.frequency.setValueAtTime(0, audioContext.currentTime);
    oscillator.connect(gainNode);
    oscillator.start();
    return {
      analyserNode,
      cleanup: () => {
        oscillator.disconnect();
        gainNode.disconnect();
        analyserNode.disconnect();
        audioContext.close();
      },
    };
  }
};

const makeTubeAnimation = (
  canvas: HTMLCanvasElement,
  size: number,
  pointSize: number,
) => {
  try {
    return new TubeAnimation3D({ canvas, size, pointSize });
  } catch {
    return new TubeAnimation2D({ canvas, size, pointSize });
  }
};

const makeZaraAnimation = (
  canvas: HTMLCanvasElement,
  size: number,
  pointSize: number,
) => {
  try {
    return new ZaraAnimation3D({ canvas, size, pointSize });
  } catch {
    return new ZaraAnimation2D({ canvas, size, pointSize });
  }
};

const ZaraAvatar = ({
  isAiSpeaking,
  avServiceAnalyser,
  isLoading = false,
  size = 140,
  pointSize = 0.5,
}: ZaraAvatarProps) => {
  const { stream } = useMediaDevicesContext();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    let audioAnalyzer: AudioAnalyzer | null = null;

    // Setup audio analyzer only if we have a stream or AI is speaking
    if (stream?.getAudioTracks().length || isAiSpeaking) {
      audioAnalyzer = setupAudioAnalyzer(stream, isAiSpeaking);
    }

    // Setup appropriate animation based on loading state
    const animation = isLoading
      ? makeTubeAnimation(canvasRef.current, size, pointSize)
      : makeZaraAnimation(canvasRef.current, size, pointSize);

    // Animation loop
    let requestAnimationFrameId: number | null = null;
    const animate = () => {
      if (isLoading) {
        // Simple rotation animation for loading state
        animation.update();
      } else if (!isLoading) {
        // Audio-reactive animation for normal state
        const currentAnalyser = isAiSpeaking
          ? avServiceAnalyser
          : audioAnalyzer?.analyserNode;

        if (currentAnalyser) {
          const audioData = new Uint8Array(currentAnalyser.frequencyBinCount);
          currentAnalyser.getByteFrequencyData(audioData);
          animation.update(audioData, isAiSpeaking);
        } else {
          animation.update();
        }
      }

      requestAnimationFrameId = requestAnimationFrame(animate);
    };

    animate();

    // Cleanup
    return () => {
      if (requestAnimationFrameId) {
        cancelAnimationFrame(requestAnimationFrameId);
      }
      animation.cleanup();
      audioAnalyzer?.cleanup();
    };
  }, [stream, isAiSpeaking, avServiceAnalyser, isLoading, size, pointSize]);

  return (
    <AvatarContainer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <AvatarCanvas ref={canvasRef} size={size} />
    </AvatarContainer>
  );
};

export default ZaraAvatar;
